import React, { useEffect, useRef, useState } from "react";
import { MapContainer, TileLayer, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet-draw/dist/leaflet.draw.css";
import "leaflet-draw";
import { toast } from "sonner";
import { MapPin, Pencil, Trash2, Save, Radar } from "lucide-react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { apiClient, getApiErrorMessage } from "../../lib/apiClient";
import brandColors from "../../brandColors";

function polygonToGeoJSON(latlngs) {
  const ring = latlngs.map((ll) => [ll.lng, ll.lat]);
  if (ring.length && (ring[0][0] !== ring[ring.length - 1][0] || ring[0][1] !== ring[ring.length - 1][1])) {
    ring.push(ring[0]);
  }
  return { type: "Polygon", coordinates: [ring] };
}

function geoJSONToLatLngs(area) {
  try {
    const parsed = typeof area === "string" ? JSON.parse(area) : area;
    // Accepts a bare Polygon, a Feature<Polygon>, or a FeatureCollection whose
    // first feature is a Polygon — the outlet's saved zone has appeared in
    // more than one of these shapes across imports/tools.
    const geometry = parsed?.type === "FeatureCollection" ? parsed?.features?.[0]?.geometry
      : parsed?.type === "Feature" ? parsed?.geometry
      : parsed;
    const ring = geometry?.coordinates?.[0];
    if (!Array.isArray(ring) || ring.length < 3) return null;
    return ring.map(([lng, lat]) => L.latLng(lat, lng));
  } catch {
    return null;
  }
}

function ZoneControls({ initialArea, onDirtyChange, onSaved }) {
  const map = useMap();
  const fgRef = useRef(null);
  const drawHandlerRef = useRef(null);
  const editHandlerRef = useRef(null);
  const [mode, setMode] = useState("idle");
  const [hasPolygon, setHasPolygon] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fg = new L.FeatureGroup().addTo(map);
    fgRef.current = fg;
    const latlngs = geoJSONToLatLngs(initialArea);
    if (latlngs) {
      const layer = L.polygon(latlngs, { color: brandColors.PRIMARY, weight: 2, fillOpacity: 0.12 });
      fg.addLayer(layer);
      setHasPolygon(true);
      try { map.fitBounds(layer.getBounds(), { padding: [40, 40], maxZoom: 15 }); } catch { /* invalid bounds */ }
    }
    const onCreated = (e) => {
      fg.clearLayers();
      fg.addLayer(e.layer);
      setHasPolygon(true);
      setMode("idle");
      onDirtyChange(true);
    };
    const onEdited = () => onDirtyChange(true);
    map.on(L.Draw.Event.CREATED, onCreated);
    map.on(L.Draw.Event.EDITED, onEdited);
    return () => {
      map.off(L.Draw.Event.CREATED, onCreated);
      map.off(L.Draw.Event.EDITED, onEdited);
      map.removeLayer(fg);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map]);

  const startDraw = () => {
    if (editHandlerRef.current) { editHandlerRef.current.disable(); editHandlerRef.current = null; }
    const handler = new L.Draw.Polygon(map, {
      showArea: false, allowIntersection: false,
      shapeOptions: { color: brandColors.PRIMARY, weight: 2, fillOpacity: 0.12 },
    });
    handler.enable();
    drawHandlerRef.current = handler;
    setMode("drawing");
  };

  const toggleEdit = () => {
    const fg = fgRef.current;
    if (!fg || fg.getLayers().length === 0) { toast.error("Draw a delivery zone first."); return; }
    if (mode === "editing") {
      editHandlerRef.current?.disable();
      editHandlerRef.current = null;
      setMode("idle");
      onDirtyChange(true);
    } else {
      const handler = new L.EditToolbar.Edit(map, { featureGroup: fg });
      handler.enable();
      editHandlerRef.current = handler;
      setMode("editing");
    }
  };

  const clearZone = () => {
    fgRef.current?.clearLayers();
    setHasPolygon(false);
    onDirtyChange(true);
  };

  const save = async () => {
    const fg = fgRef.current;
    const layers = fg?.getLayers() || [];
    setSaving(true);
    try {
      const area = layers.length
        ? JSON.stringify(polygonToGeoJSON(layers[0].getLatLngs()[0]))
        : JSON.stringify({ type: "Polygon", coordinates: [] });
      const { data } = await apiClient.put("/outlet/settings", { area });
      toast.success("Delivery zone saved.");
      onDirtyChange(false);
      onSaved?.(data);
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Could not save the delivery zone."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="absolute right-3 top-3 z-[1000] flex flex-col gap-2" data-testid="zone-map-controls">
      <Button size="sm" variant={mode === "drawing" ? "accent" : "outline"} onClick={startDraw} data-testid="zone-draw-button">
        <MapPin size={14} strokeWidth={1.5} /> {hasPolygon ? "Redraw Zone" : "Draw Zone"}
      </Button>
      <Button size="sm" variant={mode === "editing" ? "accent" : "outline"} onClick={toggleEdit} data-testid="zone-edit-button">
        <Pencil size={14} strokeWidth={1.5} /> {mode === "editing" ? "Done Editing" : "Edit Vertices"}
      </Button>
      <Button size="sm" variant="outline" onClick={clearZone} data-testid="zone-clear-button">
        <Trash2 size={14} strokeWidth={1.5} className="text-danger" /> Clear
      </Button>
      <Button size="sm" onClick={save} disabled={saving} data-testid="zone-save-button">
        <Save size={14} strokeWidth={1.5} /> {saving ? "Saving..." : "Save Zone"}
      </Button>
    </div>
  );
}

export function DeliveryZoneCard({ outlet }) {
  const [dirty, setDirty] = useState(false);

  if (!outlet) return null;
  const center = outlet.latitude && outlet.longitude
    ? [parseFloat(outlet.latitude), parseFloat(outlet.longitude)]
    : [53.5461, -113.4938];

  return (
    <Card data-testid="settings-delivery-zone-card">
      <CardContent className="p-5">
        <div className="flex items-start gap-3">
          <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
            <Radar size={18} strokeWidth={1.5} />
          </span>
          <div className="min-w-0 flex-1">
            <p className="font-heading text-sm font-semibold text-text-main">Delivery Zone</p>
            <p className="mt-0.5 text-xs text-text-muted">
              Draw the area you deliver to. Use "Edit Vertices" to drag corners into place, then Save Zone.
              {dirty && <span className="ml-1 font-semibold text-accent-hover"> Unsaved changes.</span>}
            </p>
          </div>
        </div>

        <div className="relative mt-4 h-[420px] w-full overflow-hidden rounded-md border border-border isolate" data-testid="zone-map-editor">
          <MapContainer center={center} zoom={12} style={{ height: "100%", width: "100%" }}>
            <TileLayer attribution="&copy; OpenStreetMap contributors" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            <ZoneControls initialArea={outlet.area} onDirtyChange={setDirty} />
          </MapContainer>
        </div>
      </CardContent>
    </Card>
  );
}
